// @flow
import { constants, messageFormat } from '@clubmed/utils';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';

import { type BadgeProps } from 'Components/frameworks/badge/Badge.jsx';

export const IS_DISABLED = 'isDisabled';
export const IS_ENABLED = 'isEnabled';
export const IS_SELECTED = 'isSelected';

const { PRODUCT_TYPES, SEASONS, TOUR_TYPES } = constants;
const COMFORT_EXCLUSIVE = 5;

function isCruise(type: string): boolean {
  return type === PRODUCT_TYPES.CRUISE;
}

function isJoyview(type: string): boolean {
  return type === PRODUCT_TYPES.JOYVIEW;
}

function isResort(type: string): boolean {
  return type === PRODUCT_TYPES.RESORT;
}

function isTour(type: string): boolean {
  return type === PRODUCT_TYPES.TOUR;
}

function isVilla(type: string): boolean {
  return type === PRODUCT_TYPES.VILLA;
}

function isExclusiveComfort(comfort: number): boolean {
  return comfort >= COMFORT_EXCLUSIVE;
}

function isExclusiveCollection(type: string, comfort: number): boolean {
  return isVilla(type) || isCruise(type) || isExclusiveComfort(comfort);
}

function isWinter(seasonName: ?string): boolean {
  return seasonName === SEASONS.WINTER;
}

function isSummer(seasonName: ?string): boolean {
  return seasonName === SEASONS.SUMMER;
}

function getProductBadgecolor(type: string, comfort: number, rangeId?: string): BrandColors {
  if (isTour(type)) {
    return rangeId === TOUR_TYPES.ESCAPADE ? COLORS.KIWI : COLORS.OCHER;
  }

  if (isJoyview(type)) {
    return COLORS.WATER_GREEN;
  }

  if (isExclusiveCollection(type, comfort)) {
    return COLORS.DEEP_BLUE;
  }

  return COLORS.BLUE;
}

// TODO replace with global type
type Resort = {
  +comfort?: number,
  +comfortLabel?: string,
  +badgeIcon?: string,
  +type?: string,
  +rangeId?: string,
};

function getProductBadgeIcon({
  comfort = 0,
  comfortLabel = '',
  badgeIcon,
  type = '',
  rangeId = '',
}: Resort): BadgeProps {
  const color = getProductBadgecolor(type, comfort, rangeId);
  const iconName = (badgeIcon && ICONS[badgeIcon]) || ICONS.TRIDENT1;
  const iconSize = iconName === ICONS.TRIDENT45 ? '90%' : '';

  return {
    alt: comfortLabel,
    color: COLORS.WHITE,
    colorBg: color,
    colorBorder: color,
    iconName,
    ...(iconSize ? { iconSize } : null),
  };
}

function getExclusiveCollectionProductBadgeIcon({
  comfort = 0,
  comfortLabel = '',
  badgeIcon = '',
  type = '',
  rangeId = '',
}: Resort): BadgeProps {
  const color = getProductBadgecolor(type, comfort, rangeId);
  const displayedIcon = ICONS[badgeIcon] === ICONS.TRIDENT45 ? ICONS.TRIDENT4 : badgeIcon;

  return {
    alt: comfortLabel,
    color: COLORS.WHITE,
    colorBg: color,
    colorBorder: color,
    iconName: displayedIcon,
  };
}

function getServiceAgesLabel(
  ageInMonths: $ReadOnly<{ min: number, max: ?number }>,
  i18n: CMSContent,
) {
  const { min, max } = ageInMonths;

  if (min > 23) {
    return messageFormat(i18n.booking.services.childcare.kidsAgeInYears).format({
      min: Math.floor(min / 12),
      max: max ? Math.floor(max / 12) : '',
    });
  }

  if (min < 23 && (max && max > 23)) {
    return messageFormat(i18n.booking.services.childcare.kidsAgeInMonthsAndYears).format({
      min: min,
      max: Math.floor(max / 12),
    });
  }

  return messageFormat(i18n.booking.services.childcare.kidsAgeInMonths).format({
    min: min,
    max: max,
  });
}

function getServiceAgesLabelTranslateOptions(
  ageInMonths: $ReadOnly<{ min: number, max: ?number }>,
) {
  const { min, max } = ageInMonths;

  if (min > 23) {
    return [
      'booking.services.childcare.kidsAgeInYears',
      {
        min: Math.floor(min / 12),
        max: max ? Math.floor(max / 12) : '',
      },
    ];
  }

  if (min < 23 && (max && max > 23)) {
    return [
      'booking.services.childcare.kidsAgeInMonthsAndYears',
      {
        min: min,
        max: Math.floor(max / 12),
      },
    ];
  }

  return [
    'booking.services.childcare.kidsAgeInMonths',
    {
      min: min,
      // $FlowFixMe 🤬
      max: max,
    },
  ];
}

export {
  getProductBadgecolor,
  getProductBadgeIcon,
  getExclusiveCollectionProductBadgeIcon,
  getServiceAgesLabel,
  getServiceAgesLabelTranslateOptions,
  isExclusiveCollection,
  isCruise,
  isResort,
  isJoyview,
  isTour,
  isVilla,
  isWinter,
  isSummer,
};
